import { t } from '@lingui/core/macro';

import type {
  AreaType,
  AssistiveTechnology,
  Browser,
  CloudPlatform,
  CodingAiTool,
  ComputerOs,
  DesignPrototypingTool,
  DisabilityType,
  FoodDeliveryService,
  HealthWellnessApp,
  LivingSituation,
  MilitaryServiceStatus,
  Nationality,
  OnlineBankingTool,
  OnlineLearningPlatform,
  OnlineMarketplacePlatform,
  OnlineShoppingFrequency,
  OnlineTravelBooking,
  ParticipantLanguage,
  Pet,
  ProductivitySoftware,
  ProgrammingLanguage,
  RelationshipStatus,
  SmartHomeDevice,
  SmartphoneOs,
  SocialNetworkPlatform,
  StreamingService,
  TabletOs,
  VehicleBrand,
  WorkSetting,
} from '@/api/core-api';

import {
  CompanySize,
  EducationLevel,
  EmploymentStatus,
  Ethnicities,
  Gender,
  HouseholdIncome,
  SeniorityLevel,
} from '@/api/core-api';

type LabelMap<T extends string> = Record<T, () => string>;

function labelFromMap<T extends string>(map: LabelMap<T>, value: T): string {
  return map[value]?.() ?? value;
}

const relationshipStatusLabels = {
  single: () => t`Single`,
  inRelationship: () => t`In a relationship`,
  married: () => t`Married`,
  divorced: () => t`Divorced`,
  widowed: () => t`Widowed`,
  separated: () => t`Separated`,
  domesticPartnershipCivilUnion: () => t`Domestic Partnership / Civil Union`,
  itsComplicated: () => t`It's complicated`,
  preferNotToSay: () => t`Prefer not to say`,
  other: () => t`Other`,
} satisfies LabelMap<RelationshipStatus>;

export function getRelationshipStatusLabel(value: RelationshipStatus): string {
  return labelFromMap(relationshipStatusLabels, value);
}

const livingSituationLabels = {
  alone: () => t`Alone`,
  roommates: () => t`With roommates`,
  significantOther: () => t`With significant other`,
  spouse: () => t`With spouse`,
  parents: () => t`With parents`,
  other: () => t`Other`,
} satisfies LabelMap<LivingSituation>;

export function getLivingSituationLabel(value: LivingSituation): string {
  return labelFromMap(livingSituationLabels, value);
}

const petLabels = {
  noPets: () => t`No pets`,
  dog: () => t`Dog`,
  cat: () => t`Cat`,
  bird: () => t`Bird`,
  fish: () => t`Fish`,
  reptile: () => t`Reptile`,
  amphibian: () => t`Amphibian`,
  other: () => t`Other`,
} satisfies LabelMap<Pet>;

export function getPetLabel(value: Pet): string {
  return labelFromMap(petLabels, value);
}

const languageLabels = {
  en: () => t`English`,
  es: () => t`Spanish`,
  zh: () => t`Chinese`,
  hi: () => t`Hindi`,
  ar: () => t`Arabic`,
  bn: () => t`Bengali`,
  ru: () => t`Russian`,
  pt: () => t`Portuguese`,
  ja: () => t`Japanese`,
  de: () => t`German`,
  fr: () => t`French`,
  it: () => t`Italian`,
  ko: () => t`Korean`,
  tr: () => t`Turkish`,
  pl: () => t`Polish`,
  nl: () => t`Dutch`,
  vi: () => t`Vietnamese`,
  sv: () => t`Swedish`,
  fa: () => t`Persian/ Farsi`,
  el: () => t`Greek`,
  da: () => t`Danish`,
  fi: () => t`Finnish`,
  no: () => t`Norwegian`,
  cs: () => t`Czech`,
  th: () => t`Thai`,
  ro: () => t`Romanian`,
  he: () => t`Hebrew`,
  id: () => t`Indonesian`,
  hu: () => t`Hungarian`,
  uk: () => t`Ukrainian`,
  other: () => t`Other`,
} satisfies LabelMap<ParticipantLanguage>;

export function getLanguageLabel(value: ParticipantLanguage): string {
  return labelFromMap(languageLabels, value);
}

const nationalityLabels = {
  american: () => t`American`,
  canadian: () => t`Canadian`,
  mexican: () => t`Mexican`,
  british: () => t`British`,
  french: () => t`French`,
  german: () => t`German`,
  italian: () => t`Italian`,
  spanish: () => t`Spanish`,
  dutch: () => t`Dutch`,
  swedish: () => t`Swedish`,
  norwegian: () => t`Norwegian`,
  danish: () => t`Danish`,
  finnish: () => t`Finnish`,
  polish: () => t`Polish`,
  irish: () => t`Irish`,
  portuguese: () => t`Portuguese`,
  greek: () => t`Greek`,
  swiss: () => t`Swiss`,
  austrian: () => t`Austrian`,
  belgian: () => t`Belgian`,
  czech: () => t`Czech`,
  romanian: () => t`Romanian`,
  hungarian: () => t`Hungarian`,
  chinese: () => t`Chinese`,
  indian: () => t`Indian`,
  japanese: () => t`Japanese`,
  korean: () => t`Korean`,
  filipino: () => t`Filipino`,
  vietnamese: () => t`Vietnamese`,
  thai: () => t`Thai`,
  malaysian: () => t`Malaysian`,
  singaporean: () => t`Singaporean`,
  indonesian: () => t`Indonesian`,
  pakistani: () => t`Pakistani`,
  bangladeshi: () => t`Bangladeshi`,
  australian: () => t`Australian`,
  newzealander: () => t`New Zealander`,
  brazilian: () => t`Brazilian`,
  argentinian: () => t`Argentinian`,
  colombian: () => t`Colombian`,
  chilean: () => t`Chilean`,
  peruvian: () => t`Peruvian`,
  venezuelan: () => t`Venezuelan`,
  southafrican: () => t`South African`,
  nigerian: () => t`Nigerian`,
  egyptian: () => t`Egyptian`,
  kenyan: () => t`Kenyan`,
  israeli: () => t`Israeli`,
  turkish: () => t`Turkish`,
  saudiarabian: () => t`Saudi Arabian`,
  other: () => t`Other`,
} satisfies LabelMap<Nationality>;

export function getNationalityLabel(value: Nationality): string {
  return labelFromMap(nationalityLabels, value);
}

const disabilityTypeLabels = {
  physical: () => t`Physical`,
  sensory: () => t`Sensory`,
  cognitiveorlearning: () => t`Cognitive / Learning`,
  mentalhealth: () => t`Mental health`,
  chronicillness: () => t`Chronic illness`,
  other: () => t`Other`,
  no: () => t`No disability`,
  prefernottosay: () => t`Prefer not to say`,
} satisfies LabelMap<DisabilityType>;

export function getDisabilityTypeLabel(value: DisabilityType): string {
  return labelFromMap(disabilityTypeLabels, value);
}

const areaTypeLabels = {
  urban: () => t`Urban`,
  suburban: () => t`Suburban`,
  rural: () => t`Rural`,
} satisfies LabelMap<AreaType>;

export function getAreaTypeLabel(value: AreaType): string {
  return labelFromMap(areaTypeLabels, value);
}

const militaryServiceStatusLabels = {
  neverserved: () => t`Never served`,
  currentlyserving: () => t`Currently serving`,
  veteran: () => t`Previously served`,
} satisfies LabelMap<MilitaryServiceStatus>;

export function getMilitaryServiceStatusLabel(
  value: MilitaryServiceStatus,
): string {
  return labelFromMap(militaryServiceStatusLabels, value);
}

const workSettingLabels = {
  hybrid: () => t`Hybrid`,
  inPerson: () => t`In-person`,
  remote: () => t`Remote`,
} satisfies LabelMap<WorkSetting>;

export function getWorkSettingLabel(value: WorkSetting): string {
  return labelFromMap(workSettingLabels, value);
}

const browserLabels = {
  chrome: () => t`Google Chrome`,
  edge: () => t`Microsoft Edge`,
  firefox: () => t`Mozilla Firefox`,
  internetExplorer: () => t`Internet Explorer`,
  safari: () => t`Safari`,
  other: () => t`Other`,
} satisfies LabelMap<Browser>;

export function getBrowserLabel(value: Browser): string {
  return labelFromMap(browserLabels, value);
}

const computerOsLabels = {
  macOS: () => t`macOS`,
  windows: () => t`Windows`,
  linux: () => t`Linux`,
  other: () => t`Other`,
  noComputer: () => t`No computer`,
} satisfies LabelMap<ComputerOs>;

export function getComputerOsLabel(value: ComputerOs): string {
  return labelFromMap(computerOsLabels, value);
}

const tabletOsLabels = {
  amazonFire: () => t`Amazon Fire`,
  android: () => t`Android`,
  ipadOS: () => t`iPadOS`,
  windows: () => t`Windows`,
  other: () => t`Other`,
  noTablet: () => t`No tablet`,
} satisfies LabelMap<TabletOs>;

export function getTabletOsLabel(value: TabletOs): string {
  return labelFromMap(tabletOsLabels, value);
}

const smartphoneOsLabels = {
  android: () => t`Android`,
  ios: () => t`iOS`,
  windows: () => t`Windows Phone`,
  other: () => t`Other`,
  noSmartphone: () => t`No smartphone`,
} satisfies LabelMap<SmartphoneOs>;

export function getSmartphoneOsLabel(value: SmartphoneOs): string {
  return labelFromMap(smartphoneOsLabels, value);
}

const programmingLanguageLabels = {
  javaScript: () => t`JavaScript`,
  python: () => t`Python`,
  java: () => t`Java`,
  'c#': () => t`C#`,
  'c++': () => t`C++`,
  ruby: () => t`Ruby`,
  php: () => t`PHP`,
  swift: () => t`Swift`,
  kotlin: () => t`Kotlin`,
  golang: () => t`Go`,
  r: () => t`R`,
  typeScript: () => t`TypeScript`,
  sql: () => t`SQL`,
  other: () => t`Other`,
} satisfies LabelMap<ProgrammingLanguage>;

export function getProgrammingLanguageLabel(
  value: ProgrammingLanguage,
): string {
  return labelFromMap(programmingLanguageLabels, value);
}

const assistiveTechnologyLabels = {
  jaws: () => t`JAWS`,
  nvda: () => t`NVDA`,
  voiceover: () => t`VoiceOver`,
  talkback: () => t`TalkBack`,
  narrator: () => t`Narrator`,
  zoomtext: () => t`ZoomText`,
  magnifier: () => t`Magnifier`,
  dragonnaturallyspeaking: () => t`Dragon NaturallySpeaking`,
  voicecontrol: () => t`Voice Control`,
  voiceaccess: () => t`Voice Access`,
  switchcontrol: () => t`Switch Control`,
  eyetracking: () => t`Eye tracking`,
  headmouse: () => t`Head mouse`,
  sipandpuff: () => t`Sip-and-puff`,
  onscreenkeyboard: () => t`On-screen keyboard`,
  trackball: () => t`Trackball`,
  adaptivekeyboard: () => t`Adaptive keyboard`,
  livecaption: () => t`Live Caption`,
  closedcaptions: () => t`Closed Captions`,
  naturalreader: () => t`NaturalReader`,
  readandwrite: () => t`Read&Write`,
  kurzweil: () => t`Kurzweil 3000`,
  brailledisplay: () => t`Braille display`,
  hearingaid: () => t`Hearing aid`,
  none: () => t`None`,
  other: () => t`Other`,
} satisfies LabelMap<AssistiveTechnology>;

export function getAssistiveTechnologyLabel(
  value: AssistiveTechnology,
): string {
  return labelFromMap(assistiveTechnologyLabels, value);
}

const streamingServiceLabels = {
  netflix: () => t`Netflix`,
  amazonprimevideo: () => t`Amazon Prime Video`,
  disneyplus: () => t`Disney+`,
  hulu: () => t`Hulu`,
  hbomax: () => t`HBO Max`,
  appletvplus: () => t`Apple TV+`,
  paramountplus: () => t`Paramount+`,
  peacock: () => t`Peacock`,
  youtubetv: () => t`YouTube TV`,
  youtubepremium: () => t`YouTube Premium`,
  crunchyroll: () => t`Crunchyroll`,
  discoveryplus: () => t`Discovery+`,
  showtime: () => t`Showtime`,
  starz: () => t`Starz`,
  spotify: () => t`Spotify`,
  applemusic: () => t`Apple Music`,
  youtubemusic: () => t`YouTube Music`,
  amazonmusic: () => t`Amazon Music`,
  tidal: () => t`Tidal`,
  pandora: () => t`Pandora`,
  soundcloud: () => t`SoundCloud`,
  slingtv: () => t`Sling TV`,
  fubotv: () => t`fuboTV`,
  other: () => t`Other`,
} satisfies LabelMap<StreamingService>;

export function getStreamingServiceLabel(value: StreamingService): string {
  return labelFromMap(streamingServiceLabels, value);
}

const cloudPlatformLabels = {
  aws: () => t`AWS`,
  microsoftazure: () => t`Microsoft Azure`,
  googlecloud: () => t`Google Cloud`,
  ibmcloud: () => t`IBM Cloud`,
  oraclecloud: () => t`Oracle Cloud`,
  alibabacloud: () => t`Alibaba Cloud`,
  digitalocean: () => t`DigitalOcean`,
  heroku: () => t`Heroku`,
  vercel: () => t`Vercel`,
  netlify: () => t`Netlify`,
  cloudflare: () => t`Cloudflare`,
  linode: () => t`Linode`,
  other: () => t`Other`,
} satisfies LabelMap<CloudPlatform>;

export function getCloudPlatformLabel(value: CloudPlatform): string {
  return labelFromMap(cloudPlatformLabels, value);
}

const codingAiToolLabels = {
  claudecode: () => t`Claude Code`,
  cursor: () => t`Cursor`,
  windsurf: () => t`Windsurf`,
  githubcopilot: () => t`GitHub Copilot`,
  chatgpt: () => t`ChatGPT`,
  gemini: () => t`Gemini`,
  copilotchat: () => t`Copilot Chat`,
  other: () => t`Other`,
} satisfies LabelMap<CodingAiTool>;

export function getCodingAiToolLabel(value: CodingAiTool): string {
  return labelFromMap(codingAiToolLabels, value);
}

const productivitySoftwareLabels = {
  microsoftoffice: () => t`Microsoft Office`,
  microsoft365: () => t`Microsoft 365`,
  googleworkspace: () => t`Google Workspace`,
  appleiwork: () => t`Apple iWork`,
  notion: () => t`Notion`,
  evernote: () => t`Evernote`,
  trello: () => t`Trello`,
  asana: () => t`Asana`,
  mondaydotcom: () => t`Monday.com`,
  slack: () => t`Slack`,
  microsoftteams: () => t`Microsoft Teams`,
  zoom: () => t`Zoom`,
  airtable: () => t`Airtable`,
  clickup: () => t`ClickUp`,
  basecamp: () => t`Basecamp`,
  jira: () => t`Jira`,
  confluence: () => t`Confluence`,
  dropbox: () => t`Dropbox`,
  googledrive: () => t`Google Drive`,
  other: () => t`Other`,
} satisfies LabelMap<ProductivitySoftware>;

export function getProductivitySoftwareLabel(
  value: ProductivitySoftware,
): string {
  return labelFromMap(productivitySoftwareLabels, value);
}

const designPrototypingToolLabels = {
  figma: () => t`Figma`,
  adobexd: () => t`Adobe XD`,
  sketch: () => t`Sketch`,
  invision: () => t`InVision`,
  framer: () => t`Framer`,
  axure: () => t`Axure`,
  balsamiq: () => t`Balsamiq`,
  marvel: () => t`Marvel`,
  protoio: () => t`Proto.io`,
  adobephotoshop: () => t`Adobe Photoshop`,
  adobeillustrator: () => t`Adobe Illustrator`,
  canva: () => t`Canva`,
  affinitydesigner: () => t`Affinity Designer`,
  principle: () => t`Principle`,
  origami: () => t`Origami`,
  other: () => t`Other`,
} satisfies LabelMap<DesignPrototypingTool>;

export function getDesignPrototypingToolLabel(
  value: DesignPrototypingTool,
): string {
  return labelFromMap(designPrototypingToolLabels, value);
}

const onlineMarketplacePlatformLabels = {
  amazon: () => t`Amazon`,
  ebay: () => t`eBay`,
  etsy: () => t`Etsy`,
  walmart: () => t`Walmart`,
  target: () => t`Target`,
  bestbuy: () => t`Best Buy`,
  alibaba: () => t`Alibaba`,
  aliexpress: () => t`AliExpress`,
  shopify: () => t`Shopify`,
  wayfair: () => t`Wayfair`,
  mercari: () => t`Mercari`,
  poshmark: () => t`Poshmark`,
  depop: () => t`Depop`,
  facebookmarketplace: () => t`Facebook Marketplace`,
  craigslist: () => t`Craigslist`,
  offerup: () => t`OfferUp`,
  letgo: () => t`Letgo`,
  other: () => t`Other`,
} satisfies LabelMap<OnlineMarketplacePlatform>;

export function getOnlineMarketplacePlatformLabel(
  value: OnlineMarketplacePlatform,
): string {
  return labelFromMap(onlineMarketplacePlatformLabels, value);
}

const onlineLearningPlatformLabels = {
  coursera: () => t`Coursera`,
  udemy: () => t`Udemy`,
  linkedinlearning: () => t`LinkedIn Learning`,
  skillshare: () => t`Skillshare`,
  edx: () => t`edX`,
  khanacademy: () => t`Khan Academy`,
  pluralsight: () => t`Pluralsight`,
  masterclass: () => t`MasterClass`,
  udacity: () => t`Udacity`,
  datacamp: () => t`DataCamp`,
  codecademy: () => t`Codecademy`,
  treehouse: () => t`Treehouse`,
  brilliant: () => t`Brilliant`,
  duolingo: () => t`Duolingo`,
  rosettastone: () => t`Rosetta Stone`,
  babbel: () => t`Babbel`,
  googleskillshop: () => t`Google Skillshop`,
  other: () => t`Other`,
} satisfies LabelMap<OnlineLearningPlatform>;

export function getOnlineLearningPlatformLabel(
  value: OnlineLearningPlatform,
): string {
  return labelFromMap(onlineLearningPlatformLabels, value);
}

const foodDeliveryServiceLabels = {
  ubereats: () => t`Uber Eats`,
  doordash: () => t`DoorDash`,
  grubhub: () => t`Grubhub`,
  postmates: () => t`Postmates`,
  seamless: () => t`Seamless`,
  instacart: () => t`Instacart`,
  deliveroo: () => t`Deliveroo`,
  justeat: () => t`Just Eat`,
  skipthedishes: () => t`SkipTheDishes`,
  menulog: () => t`Menulog`,
  foodpanda: () => t`foodpanda`,
  zomato: () => t`Zomato`,
  swiggy: () => t`Swiggy`,
  rappi: () => t`Rappi`,
  other: () => t`Other`,
} satisfies LabelMap<FoodDeliveryService>;

export function getFoodDeliveryServiceLabel(
  value: FoodDeliveryService,
): string {
  return labelFromMap(foodDeliveryServiceLabels, value);
}

const onlineBankingToolLabels = {
  chime: () => t`Chime`,
  varo: () => t`Varo`,
  current: () => t`Current`,
  revolut: () => t`Revolut`,
  n26: () => t`N26`,
  monzo: () => t`Monzo`,
  mint: () => t`Mint`,
  ynab: () => t`YNAB`,
  personalcapital: () => t`Personal Capital`,
  quicken: () => t`Quicken`,
  paypal: () => t`PayPal`,
  venmo: () => t`Venmo`,
  cashapp: () => t`Cash App`,
  zelle: () => t`Zelle`,
  applepay: () => t`Apple Pay`,
  googlepay: () => t`Google Pay`,
  robinhood: () => t`Robinhood`,
  webull: () => t`Webull`,
  acorns: () => t`Acorns`,
  stash: () => t`Stash`,
  betterment: () => t`Betterment`,
  wealthfront: () => t`Wealthfront`,
  coinbase: () => t`Coinbase`,
  binance: () => t`Binance`,
  kraken: () => t`Kraken`,
  other: () => t`Other`,
} satisfies LabelMap<OnlineBankingTool>;

export function getOnlineBankingToolLabel(value: OnlineBankingTool): string {
  return labelFromMap(onlineBankingToolLabels, value);
}

const healthWellnessAppLabels = {
  applefitness: () => t`Apple Fitness+`,
  fitbit: () => t`Fitbit`,
  strava: () => t`Strava`,
  peloton: () => t`Peloton`,
  niketrainingclub: () => t`Nike Training Club`,
  myfitnesspal: () => t`MyFitnessPal`,
  loseit: () => t`Lose It!`,
  headspace: () => t`Headspace`,
  calm: () => t`Calm`,
  insighttimer: () => t`Insight Timer`,
  tenpercenthappier: () => t`Ten Percent Happier`,
  betterhelp: () => t`BetterHelp`,
  talkspace: () => t`Talkspace`,
  sleepcycle: () => t`Sleep Cycle`,
  slumber: () => t`Slumber`,
  applehealth: () => t`Apple Health`,
  googlefit: () => t`Google Fit`,
  samsunghealth: () => t`Samsung Health`,
  noom: () => t`Noom`,
  weightwatchers: () => t`WeightWatchers`,
  cronometer: () => t`Cronometer`,
  other: () => t`Other`,
} satisfies LabelMap<HealthWellnessApp>;

export function getHealthWellnessAppLabel(value: HealthWellnessApp): string {
  return labelFromMap(healthWellnessAppLabels, value);
}

const socialNetworkPlatformLabels = {
  facebook: () => t`Facebook`,
  instagram: () => t`Instagram`,
  twitter: () => t`Twitter / X`,
  tiktok: () => t`TikTok`,
  linkedin: () => t`LinkedIn`,
  snapchat: () => t`Snapchat`,
  pinterest: () => t`Pinterest`,
  reddit: () => t`Reddit`,
  youtube: () => t`YouTube`,
  whatsapp: () => t`WhatsApp`,
  telegram: () => t`Telegram`,
  discord: () => t`Discord`,
  twitch: () => t`Twitch`,
  mastodon: () => t`Mastodon`,
  threads: () => t`Threads`,
  bluesky: () => t`Bluesky`,
  wechat: () => t`WeChat`,
  weibo: () => t`Weibo`,
  clubhouse: () => t`Clubhouse`,
  nextdoor: () => t`Nextdoor`,
  other: () => t`Other`,
} satisfies LabelMap<SocialNetworkPlatform>;

export function getSocialNetworkPlatformLabel(
  value: SocialNetworkPlatform,
): string {
  return labelFromMap(socialNetworkPlatformLabels, value);
}

const onlineTravelBookingLabels = {
  expedia: () => t`Expedia`,
  bookingdotcom: () => t`Booking.com`,
  airbnb: () => t`Airbnb`,
  vrbo: () => t`Vrbo`,
  kayak: () => t`Kayak`,
  priceline: () => t`Priceline`,
  hotelsdotcom: () => t`Hotels.com`,
  tripadvisor: () => t`Tripadvisor`,
  orbitz: () => t`Orbitz`,
  travelocity: () => t`Travelocity`,
  hotwire: () => t`Hotwire`,
  skyscanner: () => t`Skyscanner`,
  agoda: () => t`Agoda`,
  tripdotcom: () => t`Trip.com`,
  hopper: () => t`Hopper`,
  googleflights: () => t`Google Flights`,
  other: () => t`Other`,
} satisfies LabelMap<OnlineTravelBooking>;

export function getOnlineTravelBookingLabel(
  value: OnlineTravelBooking,
): string {
  return labelFromMap(onlineTravelBookingLabels, value);
}

const vehicleBrandLabels = {
  ford: () => t`Ford`,
  chevrolet: () => t`Chevrolet`,
  dodge: () => t`Dodge`,
  ram: () => t`Ram`,
  jeep: () => t`Jeep`,
  gmc: () => t`GMC`,
  cadillac: () => t`Cadillac`,
  tesla: () => t`Tesla`,
  rivian: () => t`Rivian`,
  lucid: () => t`Lucid`,
  toyota: () => t`Toyota`,
  honda: () => t`Honda`,
  nissan: () => t`Nissan`,
  mazda: () => t`Mazda`,
  subaru: () => t`Subaru`,
  mitsubishi: () => t`Mitsubishi`,
  lexus: () => t`Lexus`,
  acura: () => t`Acura`,
  infiniti: () => t`Infiniti`,
  hyundai: () => t`Hyundai`,
  kia: () => t`Kia`,
  genesis: () => t`Genesis`,
  bmw: () => t`BMW`,
  mercedesbenz: () => t`Mercedes-Benz`,
  audi: () => t`Audi`,
  volkswagen: () => t`Volkswagen`,
  porsche: () => t`Porsche`,
  volvo: () => t`Volvo`,
  mini: () => t`Mini`,
  landrover: () => t`Land Rover`,
  jaguar: () => t`Jaguar`,
  fiat: () => t`Fiat`,
  alfaromeo: () => t`Alfa Romeo`,
  peugeot: () => t`Peugeot`,
  renault: () => t`Renault`,
  ferrari: () => t`Ferrari`,
  lamborghini: () => t`Lamborghini`,
  maserati: () => t`Maserati`,
  bentley: () => t`Bentley`,
  rollsroyce: () => t`Rolls-Royce`,
  other: () => t`Other`,
} satisfies LabelMap<VehicleBrand>;

export function getVehicleBrandLabel(value: VehicleBrand): string {
  return labelFromMap(vehicleBrandLabels, value);
}

const smartHomeDeviceLabels = {
  amazonecho: () => t`Amazon Echo`,
  googlenest: () => t`Google Nest`,
  applehomepod: () => t`Apple HomePod`,
  echoshow: () => t`Echo Show`,
  nesthub: () => t`Nest Hub`,
  nestthermostat: () => t`Nest Thermostat`,
  ecobee: () => t`ecobee`,
  honeywellhome: () => t`Honeywell Home`,
  philipshue: () => t`Philips Hue`,
  lifx: () => t`LIFX`,
  nanoleaf: () => t`Nanoleaf`,
  ring: () => t`Ring`,
  arlo: () => t`Arlo`,
  nestcam: () => t`Nest Cam`,
  blink: () => t`Blink`,
  augustlock: () => t`August Lock`,
  tplink: () => t`TP-Link`,
  wemo: () => t`Wemo`,
  kasa: () => t`Kasa`,
  roomba: () => t`Roomba`,
  roborock: () => t`Roborock`,
  eufy: () => t`eufy`,
  smartrefrigerator: () => t`Smart Refrigerator`,
  smartoven: () => t`Smart Oven`,
  smartwasher: () => t`Smart Washer`,
  other: () => t`Other`,
} satisfies LabelMap<SmartHomeDevice>;

export function getSmartHomeDeviceLabel(value: SmartHomeDevice): string {
  return labelFromMap(smartHomeDeviceLabels, value);
}

const onlineShoppingFrequencyLabels = {
  severaltimesweek: () => t`Several times a week`,
  aboutonceweek: () => t`About once a week`,
  fewtimesmonth: () => t`A few times a month`,
  rarely: () => t`Rarely`,
  never: () => t`Never`,
} satisfies LabelMap<OnlineShoppingFrequency>;

export function getOnlineShoppingFrequencyLabel(
  value: OnlineShoppingFrequency,
): string {
  return labelFromMap(onlineShoppingFrequencyLabels, value);
}

// ── Demographics / Employment labels ────────────────────────────────────────
//
// These use a switch-based style rather than the LabelMap pattern above; they
// were originally defined in `routes/participants/.../settings/-utils.ts` and
// were lifted here so they can be reused by the researcher-side screener-
// response drawer too. Style consistency is a future cleanup.

function autoTitleCase(value: string): string {
  if (value === 'N/A') return 'N/A';
  return value
    .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

export function getGenderLabel(value: Gender): string {
  switch (value) {
    case Gender.PREFERNOTTOANSWER:
      return t`Prefer not to answer`;
    default:
      return autoTitleCase(value);
  }
}

export function getEthnicityLabel(value: Ethnicities): string {
  switch (value) {
    case Ethnicities.AMERICANINDIANALASKANNATIVE:
      return t`American Indian / Alaska Native`;
    case Ethnicities.HISPANICLATINO:
      return t`Hispanic / Latino`;
    case Ethnicities.BLACKORAFRICANAMERICAN:
      return t`Black / African American`;
    case Ethnicities.ASIANPACIFICISLANDER:
      return t`Asian / Pacific Islander`;
    case Ethnicities.WHITECAUCASIAN:
      return t`White / Caucasian`;
    case Ethnicities.MIDDLEEASTERN:
      return t`Middle Eastern`;
    case Ethnicities.MULTIPLEETHNICITY:
      return t`Mixed Ethnicity`;
    case Ethnicities.PREFERNOTDISCLOSE:
      return t`Prefer not to disclose`;
    case Ethnicities.N_A:
      return 'N/A';
  }
}

export function getEducationLevelLabel(value: EducationLevel): string {
  switch (value) {
    case EducationLevel.HIGHSCHOOLGRADUATE:
      return t`High School Graduate / Equivalent`;
    case EducationLevel.SOMECOLLEGENODEGREE:
      return t`Some College / University, no degree`;
    case EducationLevel.TRADETECHNICALVOCATIONALTRAINING:
      return t`Trade / Technical / Vocational Training`;
    case EducationLevel.ASSOCIATEDEGREE:
      return t`Associate Degree`;
    case EducationLevel.BACHELORDEGREE:
      return t`Bachelor's Degree`;
    case EducationLevel.MASTERDEGREE:
      return t`Master's Degree`;
    case EducationLevel.DOCTORATEDEGREE:
      return t`Doctorate Degree`;
    case EducationLevel.SOMEHIGHSCHOOL:
      return t`Some High School / Equivalent`;
    case EducationLevel.N_A:
      return 'N/A';
  }
}

export function getHouseholdIncomeLabel(value: HouseholdIncome): string {
  switch (value) {
    case HouseholdIncome['<30000']:
      return t`Under $30,000`;
    case HouseholdIncome['30000_39999']:
      return '$30,000 - $39,999';
    case HouseholdIncome['40000_59999']:
      return '$40,000 - $59,999';
    case HouseholdIncome['60000_79999']:
      return '$60,000 - $79,999';
    case HouseholdIncome['80000_99999']:
      return '$80,000 - $99,999';
    case HouseholdIncome['100000_149999']:
      return '$100,000 - $149,999';
    case HouseholdIncome['150000_199999']:
      return '$150,000 - $199,999';
    case HouseholdIncome['200000']:
      return '$200,000 or more';
    default:
      return value;
  }
}

export function getEmploymentStatusLabel(value: EmploymentStatus): string {
  switch (value) {
    case EmploymentStatus.OWNER:
      return t`Business Owner`;
    case EmploymentStatus.EMPLOYEDFULLTIME:
      return t`Employed Full-Time`;
    case EmploymentStatus.EMPLOYEDPARTTIME:
      return t`Employed Part-Time`;
    case EmploymentStatus.UNEMPLOYEDLOOKINGFORWORK:
      return t`Unemployed, Looking for Work`;
    case EmploymentStatus.UNEMPLOYED:
      return t`Unemployed`;
    case EmploymentStatus.RETIRED:
      return t`Retired`;
    case EmploymentStatus.DISABLED:
      return t`Disabled`;
    case EmploymentStatus.HOMEMAKER:
      return t`Homemaker`;
    case EmploymentStatus.FULLTIMESTUDENT:
      return t`Full-Time Student`;
    case EmploymentStatus.PARTTIMESTUDENT:
      return t`Part-Time Student`;
  }
}

export function getSeniorityLevelLabel(value: SeniorityLevel): string {
  switch (value) {
    case SeniorityLevel.UNPAID:
      return t`Unpaid`;
    case SeniorityLevel.STUDENT:
      return t`Student`;
    case SeniorityLevel.TRAINING:
      return t`Training`;
    case SeniorityLevel.ENTRYLEVEL:
      return t`Entry Level`;
    case SeniorityLevel.SENIOR:
      return t`Senior`;
    case SeniorityLevel.MANAGER:
      return t`Manager`;
    case SeniorityLevel.DIRECTOR:
      return t`Director`;
    case SeniorityLevel.CHIEFOFFICER:
      return t`Chief Officer`;
    case SeniorityLevel.PARTNER:
      return t`Partner`;
    case SeniorityLevel.VICEPRESIDENT:
      return t`Vice President`;
  }
}

export function getCompanySizeLabel(value: CompanySize): string {
  switch (value) {
    case CompanySize.SELFEMPLOYED:
      return t`Self-Employed`;
    case CompanySize['1_10']:
      return '1-10';
    case CompanySize['11_50']:
      return '11-50';
    case CompanySize['51_200']:
      return '51-200';
    case CompanySize['201_500']:
      return '201-500';
    case CompanySize['501_1000']:
      return '501-1,000';
    case CompanySize['1001_5000']:
      return '1,001-5,000';
    case CompanySize['5001_10000']:
      return '5,001-10,000';
    case CompanySize[10_000]:
      return '10,000+';
    case CompanySize.ALL:
      return t`All Company Sizes`;
  }
}
